/*----------------------------------
- DEPENDANCES
----------------------------------*/

// Npm
import React from 'react';
import type { JSX } from 'preact';

// Icons (Lucide)
import {
    AlertTriangle,
    AlarmClock,
    ArrowDownToLine,
    ArrowLeft,
    ArrowRight,
    AtSign,
    Banknote,
    Ban,
    Briefcase,
    Brush,
    Building,
    Check,
    CheckCircle,
    CalendarCheck,
    ChevronDown,
    ChevronUp,
    Chrome,
    Clock,
    Coins,
    Copy,
    Crown,
    DollarSign,
    Download,
    ExternalLink,
    Eye,
    File,
    FileSpreadsheet,
    GraduationCap,
    HandCoins,
    Heart,
    HelpCircle,
    Hourglass,
    Info,
    LayoutGrid,
    Lightbulb,
    Linkedin,
    Loader2,
    Magnet,
    MapPin,
    Mail,
    MessageCircle,
    MessageSquare,
    MessagesSquare,
    Minus,
    MinusCircle,
    MoreHorizontal,
    MousePointer,
    Pencil,
    Pen,
    Phone,
    PlaneTakeoff,
    Play,
    Plug,
    Plus,
    PlusCircle,
    Rocket,
    Search,
    Send,
    Settings,
    Shield,
    ShieldCheck,
    ShieldUser,
    SquareStop,
    Star,
    ThumbsUp,
    Trash2,
    TrendingUp,
    Trophy,
    UserPlus,
    Users,
    WandSparkles,
    Wind,
    X,
    XCircle,
    Zap,
} from 'lucide-preact';
import type { LucideIcon, LucideProps } from 'lucide-preact';

/*----------------------------------
- CONFIG
----------------------------------*/

/**
 * Centralized mapping of the legacy icon naming (previously used by the `i[src="..."]` pattern)
 * to Lucide icons.
 *
 * This lets us migrate progressively while keeping:
 * - The existing `<i>` wrapper styling from 5HTP (sizes, alignment, `.solid`, etc.)
 * - A single source of truth for legacy names (avoids duplicated mapping across the codebase)
 */
const ICON_BY_NAME: Record<string, LucideIcon> = {
    // Navigation / arrows
    'angle-down': ChevronDown,
    'angle-up': ChevronUp,
    'arrow-left': ArrowLeft,
    'arrow-right': ArrowRight,
    'arrow-to-bottom': ArrowDownToLine,
    'ellipsis-h': MoreHorizontal,
    'long-arrow-left': ArrowLeft,
    'long-arrow-right': ArrowRight,

    // Actions
    check: Check,
    'check-circle': CheckCircle,
    copy: Copy,
    download: Download,
    edit: Pencil,
    envelope: Mail,
    'exclamation-triangle': AlertTriangle,
    eye: Eye,
    file: File,
    'file-excel': FileSpreadsheet,
    minus: Minus,
    'minus-circle': MinusCircle,
    'paper-plane': Send,
    pen: Pen,
    pencil: Pencil,
    play: Play,
    plug: Plug,
    plus: Plus,
    'plus-circle': PlusCircle,
    search: Search,
    stop: SquareStop,
    times: X,
    'times-circle': XCircle,
    trash: Trash2,
    'user-plus': UserPlus,

    // Brands (FontAwesome used `brands/...`, Lucide doesn't ship brand icons)
    'brands/google': Chrome,
    'brands/linkedin': Linkedin,
    'brands/microsoft': LayoutGrid,
    'brands/whatsapp': MessageCircle,

    // Misc UI
    'alarm-clock': AlarmClock,
    at: AtSign,
    ban: Ban,
    bolt: Zap,
    briefcase: Briefcase,
    broom: Brush,
    building: Building,
    'calendar-check': CalendarCheck,
    'chart-line': TrendingUp,
    clock: Clock,
    cog: Settings,
    coin: Coins,
    coins: Coins,
    crown: Crown,
    'dollar-sign': DollarSign,
    'external-link': ExternalLink,
    'graduation-cap': GraduationCap,
    heart: Heart,
    hourglass: Hourglass,
    'info-circle': Info,
    lightbulb: Lightbulb,
    magnet: Magnet,
    magic: WandSparkles,
    'map-marker-alt': MapPin,
    'money-bill-wave': Banknote,
    'mouse-pointer': MousePointer,
    'plane-departure': PlaneTakeoff,
    'question-circle': HelpCircle,
    rocket: Rocket,
    'sack-dollar': HandCoins,
    'shield-alt': Shield,
    'shield-check': ShieldCheck,
    star: Star,
    'thumbs-up': ThumbsUp,
    trophy: Trophy,
    'user-shield': ShieldUser,
    users: Users,
    wind: Wind,

    // Messaging
    'comment-alt': MessageSquare,
    'comments-alt': MessagesSquare,

    // Loaders
    spin: Loader2,
};

const normalizeIconName = (name: string): string => {
    // The legacy codebase used optional prefixes like `solid/` and `regular/`
    // for FontAwesome styles. With Lucide, the icon is enough: keep only the icon name.
    return name
        .trim()
        .replace(/^(solid|regular)\//, '')
        .toLowerCase();
};

/*----------------------------------
- TYPES
----------------------------------*/

export type IconProps = Omit<JSX.HTMLAttributes<HTMLElement>, 'children'> & {
    name?: string;
    size?: LucideProps['size'];
    strokeWidth?: LucideProps['strokeWidth'];
    color?: LucideProps['color'];
    absoluteStrokeWidth?: LucideProps['absoluteStrokeWidth'];
};

/*----------------------------------
- COMPONENT
----------------------------------*/

export default ({
    name,
    size = '1em',
    strokeWidth,
    color,
    absoluteStrokeWidth,
    class: className,
    className: reactClassName,
    ...attrs
}: IconProps) => {
    // No name means no icon.
    if (!name) return null;

    const normalizedName = normalizeIconName(name);

    // Fallback to HelpCircle to avoid runtime crashes if an unexpected legacy icon is used.
    const Lucide = ICON_BY_NAME[normalizedName] ?? HelpCircle;

    // Merge both `class` (preact) and `className` (react compat) into a single `class`
    // to keep behavior predictable across the legacy + modern parts of the codebase.
    let mergedClassName = [className, reactClassName].filter(Boolean).join(' ');

    // Preserve the old UX for "spin" by automatically adding the `.spin` class.
    // 5HTP defines a `@keyframes spin` + `i.spin { ... }`, so rotating the wrapper keeps
    // behavior consistent across the legacy UI without tailwind dependencies.
    if (normalizedName === 'spin') {
        if (!mergedClassName.includes('spin')) mergedClassName = (mergedClassName + ' spin').trim();
    }

    return (
        <i {...attrs} class={mergedClassName || undefined} data-icon={normalizedName}>
            <Lucide size={size} strokeWidth={strokeWidth} color={color} absoluteStrokeWidth={absoluteStrokeWidth} />
        </i>
    );
};
